import api from "../../../api/api";
import { responseError } from "../../../api/apiError";
import { getFieldValue, type Item } from "../ReportServices";

export const fetchDropdownData = async (
  call: string,
  idField: string,
  nameField: string,
  sorted: boolean,
  authToken: string,
  identityProvider: string
): Promise<Item[]> => {
  let callParts = call.split("&");
  callParts = callParts.map((part) => part.replace(/%26/g, "&"));

  let callEndpoint = callParts[0];
  callParts = callParts.slice(1);

  let body: any = {
    apiEndpoint: callEndpoint,
  };

  for (let part of callParts) {
    let eqIndex = part.indexOf("=");
    if (eqIndex > 0) {
      let key = part.substring(0, eqIndex);
      let value = part.substring(eqIndex + 1);
      body[key] = value;
    } else {
      body[part] = true;
    }
  }

  const response = await api.post(`dropdown/data`, body, authToken, identityProvider);
  if (!response.ok) throw await responseError(response);
  const data: any = await response.json();
  let rowItems: Item[] = data.rows
    .map((row: any) => ({ id: getFieldValue(row, idField), name: getFieldValue(row, nameField) }))
    .filter((item: any) => item.id != null && item.name != null);
  rowItems = Array.from(
    new Map(rowItems.map((obj: Item) => [obj.id, obj])).values()
  );
  if (sorted) {
    rowItems.sort((a, b) => a.name.localeCompare(b.name));
  }
  return rowItems;
};

export const fetchReportData = async (
  body: any,
  authToken: string,
  identityProvider: string
): Promise<{ rows: any[]; totalCount: number }> => {
  const response = await api.post(
    "report/data",
    body,
    authToken,
    identityProvider
  );
  if (!response.ok) throw await responseError(response);
  const data: any = await response.json();
  return {
    rows: data.rows,
    totalCount: data.totalCount ?? 0,
  };
};

export const checkReportFileExists = async (
  fileName: string,
  templateName: string,
  authToken: string,
  identityProvider: string
): Promise<boolean> => {
  const response = await api.fetch(
    `report/file/exists?FileName=${encodeURIComponent(
      fileName
    )}.xlsx&TemplateName=${encodeURIComponent(templateName)}`,
    authToken,
    identityProvider
  );
  return response.ok && response.status !== 404;
};

export const saveReportFile = async (
  fileName: string,
  templateName: string,
  overWrite: string,
  fileContent: ArrayBuffer,
  authToken: string
): Promise<void> => {
  const response = await api.post(
    `report/file?FileName=${encodeURIComponent(
      fileName
    )}&TemplateName=${encodeURIComponent(
      templateName
    )}&OverWrite=${encodeURIComponent(overWrite)}`,
    {
      fileContent,
      lastModified: new Date().toISOString(),
    },
    authToken
  );
  if (!response.ok) {
    throw await responseError(response);
  }
};

export const refreshAllPivotTables = async (): Promise<void> => {
  await Excel.run(async (context) => {
    const sheets = context.workbook.worksheets;
    sheets.load("items");
    await context.sync();

    for (const sheet of sheets.items) {
      sheet.pivotTables.load("items");
    }
    await context.sync();

    for (const sheet of sheets.items) {
      for (const pivot of sheet.pivotTables.items) {
        pivot.refresh();
      }
    }

    await context.sync();
  });
};

export const refreshAllQueries = async (): Promise<boolean> => {
  try {
    return await Excel.run(async (context) => {
      const queries = context.workbook.queries;
      queries.load("items");
      await context.sync();

      if (queries.items.length === 0) {
        return false;
      }

      if (Office.context.platform === Office.PlatformType.OfficeOnline) {
        return true;
      }

      context.workbook.dataConnections.refreshAll();
      await context.sync();

      return false;
    });
  } catch (error) {
    console.log("Could not refresh workbook queries automatically:", error);
    return true;
  }
};

export const buildApiBody = (
  callParts: string[],
  pageIndex?: number,
  pageSize?: number
): any => {
  let callEndpoint = callParts[0];
  let body: any = {
    apiEndpoint: callEndpoint,
  };

  if (pageIndex !== undefined) {
    body.pageIndex = pageIndex;
  }
  if (pageSize !== undefined) {
    body.pageSize = pageSize;
  }

  let remainingParts = pageIndex !== undefined ? callParts.slice(1) : callParts.slice(1);

  for (let part of remainingParts) {
    let eqIndex = part.indexOf("=");
    if (eqIndex > 0) {
      let key = part.substring(0, eqIndex);
      let value = part.substring(eqIndex + 1);
      body[key] = value;
    } else {
      body[part] = true;
    }
  }

  return body;
};
